import { useRef, useState } from 'react'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { PageContainer } from '../components/PageContainer'
import { progressRepository } from '../../persistence/progressRepository'
import { applyMotionPreference } from '../motion'
import type { ProgressState, ReducedMotionPreference } from '../../domain/progress'
import styles from './screens.module.css'

/**
 * Settings: reduced motion, preferred duration, and export / import / reset.
 * The data controls are wired directly to the progress repository interface,
 * which is the primary consumer of the persistence layer in the shell.
 */
export function SettingsScreen() {
  const [state, setState] = useState<ProgressState>(() => progressRepository.load())
  const [status, setStatus] = useState<string>('')
  const importInputRef = useRef<HTMLInputElement>(null)

  function persist(next: ProgressState) {
    progressRepository.save(next)
    setState(next)
  }

  function updateDuration(minutes: number) {
    persist({ ...state, preferences: { ...state.preferences, lessonDurationMinutes: minutes } })
    setStatus('Lesson length saved.')
  }

  function updateReducedMotion(value: ReducedMotionPreference) {
    persist({ ...state, preferences: { ...state.preferences, reducedMotion: value } })
    applyMotionPreference(value)
    setStatus('Motion preference saved.')
  }

  function handleExport() {
    const json = progressRepository.export()
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'daily-learning-progress.json'
    anchor.click()
    URL.revokeObjectURL(url)
    setStatus('Progress exported.')
  }

  async function handleImportFile(file: File) {
    try {
      const text = await file.text()
      setState(progressRepository.import(text))
      setStatus('Progress imported.')
    } catch {
      setStatus('Import failed: the file was not valid progress JSON.')
    }
  }

  function handleReset() {
    if (!window.confirm('Reset all progress and preferences? This cannot be undone.')) return
    setState(progressRepository.reset())
    setStatus('Progress reset.')
  }

  return (
    <PageContainer title="Settings" lede="Preferences and your local learning data.">
      <Card>
        <div className={styles.field}>
          <label htmlFor="duration">Preferred lesson length</label>
          <select
            id="duration"
            className={styles.control}
            value={state.preferences.lessonDurationMinutes}
            onChange={(event) => updateDuration(Number(event.target.value))}
          >
            <option value={7}>7 minutes</option>
            <option value={9}>9 minutes</option>
            <option value={12}>12 minutes</option>
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="reduced-motion">Motion</label>
          <select
            id="reduced-motion"
            className={styles.control}
            value={state.preferences.reducedMotion}
            onChange={(event) => updateReducedMotion(event.target.value as ReducedMotionPreference)}
          >
            <option value="system">Match my system</option>
            <option value="reduce">Reduce motion</option>
            <option value="no-preference">Allow motion</option>
          </select>
        </div>
      </Card>

      <Card>
        <h2 className={styles.cardHeading}>Your data</h2>
        <p className={styles.cardNote}>
          Progress is stored only in this browser. Export a backup or move it to another device.
        </p>
        <div className={styles.actions}>
          <Button variant="secondary" type="button" onClick={handleExport}>
            Export JSON
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={() => importInputRef.current?.click()}
          >
            Import JSON
          </Button>
          <Button variant="danger" type="button" onClick={handleReset}>
            Reset progress
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json"
            className="visually-hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void handleImportFile(file)
              event.target.value = ''
            }}
          />
        </div>
      </Card>

      <p className={styles.status} role="status" aria-live="polite">
        {status}
      </p>
    </PageContainer>
  )
}
